import {
  computed,
  inject,
  Injectable,
  resource,
  ResourceRef,
  Signal,
} from '@angular/core';
import {
  EUserRight,
  USER_GUEST_ID,
  UserDto,
  UserDtoWithProfilePictureBuffer,
} from '@web/dtos';
import { IApiResponse } from '@web/shared-types';
import { firstValueFrom, Observable } from 'rxjs';
import { UserGateway } from './user.gateway';
import { AuthProviderFactory } from '../../../auth/auth-provider.factory';
import { AuthProvider } from '../../../auth/auth.provider';

@Injectable({
  providedIn: 'root',
})
export class UserStore {
  private readonly authFactory: AuthProviderFactory =
    inject(AuthProviderFactory);
  private readonly auth: AuthProvider = this.authFactory.getProvider('shop');

  private readonly gateway: UserGateway = new UserGateway();
  private readonly guestId: string = this.getGuestId();

  #userResource: ResourceRef<
    IApiResponse<UserDto | UserDtoWithProfilePictureBuffer> | undefined
  > = resource({
    loader: async (): Promise<
      IApiResponse<UserDto | UserDtoWithProfilePictureBuffer> | undefined
    > => {
      await this.auth.initialize();
      if (!this.auth.isLoggedIn()) {
        return undefined;
      }
      return await firstValueFrom(this.gateway.get());
    },
  });

  readonly user: Signal<UserDto | undefined | UserDtoWithProfilePictureBuffer> =
    computed((): UserDto | undefined | UserDtoWithProfilePictureBuffer =>
      this.#userResource.hasValue()
        ? this.#userResource.value()?.data
        : undefined
    );
  readonly userOrGuestId: Signal<string> = computed((): string =>
    !this.isLoading() ? (this.user()?.userId ?? this.guestId) : ''
  );
  readonly isLoading: Signal<boolean> = computed((): boolean =>
    this.#userResource.isLoading()
  );

  deleteAccount(): Observable<IApiResponse<UserDto>> {
    const deleteRequest: Observable<IApiResponse<UserDto>> =
      this.gateway.delete(this.user()!);
    this.auth.logout();
    return deleteRequest;
  }

  updateUser(formData: FormData): void {
    firstValueFrom(this.gateway.put(formData)).then(
      (result: IApiResponse<UserDto>): void => {
        if (result && result.data) {
          this.updateUserResource(result.data);
        }
        if (result && result.message?.params?.['body']) {
          this.updateUserResource(result.message!.params['body']);
        }
      }
    );
  }

  updateUserResource(query: UserDto): void {
    this.#userResource.update((): IApiResponse<UserDto> => {
      return {
        data: query,
      };
    });
  }

  private getGuestId(): string {
    const guestID: string | null = localStorage.getItem(USER_GUEST_ID);
    if (guestID) {
      localStorage.removeItem(USER_GUEST_ID);
      return guestID;
    }
    return crypto.randomUUID();
  }

  rights(): EUserRight[] {
    return [EUserRight.CmsAdmin];
  }
}
